<template>
    <div >
        <b-modal
            v-model="handoffBuildChoiceVisible"
            modal-class="builder-handoff-choice"
            hide-header
            hide-footer
            centered
            no-close-on-backdrop
            no-close-on-esc
        >
            <section class="handoff-choice" aria-labelledby="handoff-choice-title">
                <span class="handoff-choice__eyebrow">Build already in progress</span>
                <h3 id="handoff-choice-title">What should we do with your current build?</h3>
                <p>
                    You already have {{ handoffBuildPartCount }} {{ handoffBuildPartCount == 1 ? 'part' : 'parts' }} selected.
                    To start with <strong>{{ partName(handoffPendingItem) }}</strong>, save that build first or begin with a clean checklist.
                </p>
                <div class="handoff-choice__actions">
                    <button type="button" class="handoff-choice__cancel" @click="cancelHandoffChoice">Keep current build</button>
                    <button type="button" class="handoff-choice__secondary" @click="startCleanHandoff">Start clean</button>
                    <button type="button" class="handoff-choice__primary" @click="saveCurrentBuildFirst">
                        {{ loggedIn == 'true' ? 'Save current build first' : 'Sign in to save first' }}
                    </button>
                </div>
            </section>
        </b-modal>
        <div class="builder-catalog-content">
            <!-- Window with an enlarged image -->
             <Popup v-if="popupSwitch == true" :contentType="'slider'" :items="filteredItems" />
            <Enlarge />
            <!-- the the middle section of the page - Slider, List, Search-->
            <!-- Header -->
            <div class="brands">
                <h4 ref="type" style="color: #1997c6 !important">SELECT {{getType() == 'cpu' ? 'Processor' : getType()}}</h4>
            </div>
            <section v-if="showHandoffNextStep" class="handoff-next-step" aria-live="polite">
                <span class="handoff-next-step__kicker">Next step</span>
                <div>
                    <strong>{{ handoffNextTitle }}</strong>
                    <p>{{ handoffNextDescription }}</p>
                </div>
            </section>
            <div class="searchbar-section">
                <div class="catalog-controls" :class="{ 'filters-expanded': catalogFiltersExpanded }">
                    <!-- Searchbar -->
                    <form v-on:submit.prevent="onSubmit" class="searchbar">
                        <input v-model="search" class="form-control" type="text" data-action="grow" placeholder="Search">
                    </form>
                    <BuilderFilters
                        v-if="isItemsReady && catalogFilterGroups.length"
                        class="catalog-filters"
                        :groups="catalogFilterGroups"
                        :selected="activeCatalogFilters"
                        :result-count="filteredItems.length"
                        :total-count="totalCatalogItems"
                        :type-label="partTypeLabel"
                        @toggle="toggleCatalogFilter"
                        @clear="clearCatalogFilters"
                        @expand-change="catalogFiltersExpanded = $event"
                    />
                </div>
                <!-- NAVBAR -->
                <smallnav v-if="getFetchedItems" :viewModes="viewModes" :items="filteredItems"/>
            </div>
            <!-- ADVANCED -->
            <div v-if="getLayout == 'advanced'" class="advanced">
                <div class="left-side-page">
                    <!--Main Active Frame -->
                    <div class="items-display builder-display-frame" :class="{ 'builder-loading': isCategoryLoading }">
                        <div class="builder-content-underlay" :class="{ 'is-dimmed': isCategoryLoading }">
                            <!-- SLIDER -->
                            <div v-if="this.viewModes[0].chosen == true && filteredItems.length > 0" class="slider-container">
                                <Slider
                                v-if="this.viewModes[0].chosen == true"
                                class="slider"
                                :items="filteredItems"/>
                            </div>
                            <!-- The list used to carry its own search box here, bound
                                 to the same `search` model as the one in the toolbar
                                 above. Duplicate control, and it made List taller
                                 than Details by exactly its own height. -->
                            <!-- LIST -->
                            <List v-if="this.viewModes[1].chosen == true && filteredItems.length > 0" :mode="'cpu'" :items="filteredItems" :search="search"/>
                            <div v-if="isItemsReady && filteredItems.length == 0" class="builder-empty-state">
                                <template v-if="hasCompatibilityHiddenResults">
                                    <div class="builder-empty-state__eyebrow">Incompatible Results Found</div>
                                    <div class="builder-empty-state__title">
                                        Compatible Only hid
                                        <button
                                            type="button"
                                            class="builder-empty-state__count"
                                            title="Show incompatible results"
                                            @click="showIncompatibleResults"
                                        >{{ compatibilityHiddenCount }} result{{ compatibilityHiddenCount == 1 ? '' : 's' }}</button>
                                    </div>
                                    <p>
                                        Matching {{ partTypeLabel }} exist, but they are incompatible with parts currently in your build.
                                        Turn off Compatible Only if you still want to review them.
                                    </p>
                                    <div v-if="compatibilityBlockers.length" class="compatibility-blockers">
                                        <div class="compatibility-blockers__label">Conflicts with selected build parts</div>
                                        <div class="compatibility-blockers__list">
                                            <div v-for="blocker in compatibilityBlockers" :key="blocker.key" class="compatibility-blocker">
                                                <div class="compatibility-blocker__identity">
                                                    <span>{{ blocker.typeLabel }}</span>
                                                    <strong :title="blocker.name">{{ blocker.name }}</strong>
                                                </div>
                                                <div class="compatibility-blocker__specs">
                                                    <span
                                                        v-for="spec in blocker.specs"
                                                        :key="spec.key"
                                                        class="compatibility-blocker__spec"
                                                        :title="spec.message"
                                                    >
                                                        <span>{{ spec.label }}</span>
                                                        <strong v-if="spec.value">{{ spec.value }}</strong>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="builder-empty-state__actions">
                                        <button type="button" class="builder-empty-state__button" @click="showIncompatibleResults">Show Incompatible Results</button>
                                    </div>
                                    <small class="builder-empty-state__note">Compatibility warnings will remain visible.</small>
                                </template>
                                <template v-else>
                                    <div class="builder-empty-state__eyebrow">No Results</div>
                                    <div class="builder-empty-state__title">No matching parts found</div>
                                    <p>No {{ partTypeLabel }} match <span v-if="searchTerm">"{{ searchTerm }}"</span>.</p>
                                    <div class="builder-empty-state__actions">
                                        <button v-if="searchTerm" type="button" class="builder-empty-state__button" @click="clearSearch">Clear Search</button>
                                        <button v-if="hasActiveCatalogFilters" type="button" class="builder-empty-state__button" @click="clearCatalogFilters">Clear Filters</button>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <LoadingOverlay v-if="isCategoryLoading" />
                    </div>
                </div>
            </div>
            <!-- SIMPLE -->
            <div style="min-height: 550px; position: relative"  v-if="getLayout == 'simple' || getLayout == 'album'" class="demo builder-display-frame" :class="{ 'builder-loading': isCategoryLoading }">
                <div class="builder-content-underlay" :class="{ 'is-dimmed': isCategoryLoading }">
                    <!-- LIST -->
                    <List v-if="filteredItems.length > 0"  :mode="'cpu'" :layout="'demo'" :items="filteredItems" :search="search"/>
                    <div v-else-if="isItemsReady" class="builder-empty-state">
                        <template v-if="hasCompatibilityHiddenResults">
                            <div class="builder-empty-state__eyebrow">Incompatible Results Found</div>
                            <div class="builder-empty-state__title">
                                Compatible Only hid
                                <button
                                    type="button"
                                    class="builder-empty-state__count"
                                    title="Show incompatible results"
                                    @click="showIncompatibleResults"
                                >{{ compatibilityHiddenCount }} result{{ compatibilityHiddenCount == 1 ? '' : 's' }}</button>
                            </div>
                            <p>
                                Matching {{ partTypeLabel }} exist, but they are incompatible with parts currently in your build.
                                Turn off Compatible Only if you still want to review them.
                            </p>
                            <div v-if="compatibilityBlockers.length" class="compatibility-blockers">
                                <div class="compatibility-blockers__label">Conflicts with selected build parts</div>
                                <div class="compatibility-blockers__list">
                                    <div v-for="blocker in compatibilityBlockers" :key="blocker.key" class="compatibility-blocker">
                                        <div class="compatibility-blocker__identity">
                                            <span>{{ blocker.typeLabel }}</span>
                                            <strong :title="blocker.name">{{ blocker.name }}</strong>
                                        </div>
                                        <div class="compatibility-blocker__specs">
                                            <span
                                                v-for="spec in blocker.specs"
                                                :key="spec.key"
                                                class="compatibility-blocker__spec"
                                                :title="spec.message"
                                            >
                                                <span>{{ spec.label }}</span>
                                                <strong v-if="spec.value">{{ spec.value }}</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="builder-empty-state__actions">
                                <button type="button" class="builder-empty-state__button" @click="showIncompatibleResults">Show Incompatible Results</button>
                            </div>
                            <small class="builder-empty-state__note">Compatibility warnings will remain visible.</small>
                        </template>
                        <template v-else>
                            <div class="builder-empty-state__eyebrow">No Results</div>
                            <div class="builder-empty-state__title">No matching parts found</div>
                            <p>No {{ partTypeLabel }} match <span v-if="searchTerm">"{{ searchTerm }}"</span>.</p>
                            <div class="builder-empty-state__actions">
                                <button v-if="searchTerm" type="button" class="builder-empty-state__button" @click="clearSearch">Clear Search</button>
                                <button v-if="hasActiveCatalogFilters" type="button" class="builder-empty-state__button" @click="clearCatalogFilters">Clear Filters</button>
                            </div>
                        </template>
                    </div>
                </div>
                <LoadingOverlay v-if="isCategoryLoading" />
                <!-- <div v-if="(allItemsLoaded || allItemsLoaded == '') && listExtended == false && filteredItems && filteredItems.length >4"  :mode="'cpu'" :layout="'demo'" class="extend-list-button" @click="extendList()"><i class="icon icon-chevron-thin-down"></i></div> -->
                <!-- <div v-if="(allItemsLoaded || allItemsLoaded == '') && listExtended == true && filteredItems && filteredItems.length >4"  :mode="'cpu'" :layout="'demo'" class="extend-list-button" @click="extendList()"><i class="icon icon-chevron-thin-up"></i></div> -->
            </div>
            <!-- COMPARE -->
            <div style="position: relative"  v-if="getLayout == 'compare'" class="demo">
                <!-- LIST -->
                <LoadingOverlay v-if="!allItemsLoaded" mode="block" min-height="320px" />
                <Compare v-if="allItemsLoaded || allItemsLoaded == ''"  :mode="'cpu'" :items="getCompareList" />   
            </div>
        </div>  
    </div>
</template>

<script>
import { mapGetters }           from 'vuex';
import { mapActions }           from 'vuex'
import Slider                   from './Slider.vue'
import { EventBus }             from '../../eventBus'
import smallnav                 from './smallnav.vue'
import Enlarge                  from '../default/Enlarge'
import List                     from './List'
import LoadingOverlay           from '../widgets/LoadingOverlay.vue'
import Popup                    from '../Popup.vue'
import {functions}              from '../../functions'
import Compare                  from './Compare.vue'
import BuilderFilters           from './BuilderFilters.vue'
import analytics                from '../../services/analytics'
import { isPartAvailable }      from '../../utils/partAvailability'
import { getPartDefinition }    from '../../partOrder'

export default {
    name: "CPU",   
    components: {
        Slider,
        smallnav,
        Enlarge,
        List,
        LoadingOverlay,
        Popup,
        Compare,
        BuilderFilters
    },
    watch: {
        '$route.name': function(newName, oldName){
            if(newName && newName !== oldName && this.isBuilderPartRoute(newName)){
                this.search = ''
                this.activeCatalogFilters = {}
                this.loadCategory(newName)
                this.syncLayoutFromRoute()
            }
        },
        '$route.query.view': function(){
            this.syncLayoutFromRoute()
        },
        '$route.query.redir': function(){
            this.syncLayoutFromRoute()
        },
        '$route.query.handoff': function(){
            this.processBuilderHandoff()
        },
        getFetchedItems: function(){
            this.syncLocalItems()
            this.processBuilderHandoff()
        },
        allItemsLoaded: function(){
            this.processBuilderHandoff()
        },
    },
    data() {
        return {
            result: {},
            compareList: null,
            search: "",
            type: this.$route.path.replace(/\\|\/builder\//g,'') ,
            activeCatalogFilters: {},
            catalogFiltersExpanded: false,
            viewModes: [
                {
                    id: 1,
                    name: "Slider",
                    chosen: true
                },
                {
                    id: 2,
                    name: "List",
                    chosen: false
                }
            ],
            attribs: [],
            items: this.getItems,
            popupSwitch: false,
            listExtended: false,
            handoffProcessing: false,
            processedHandoffKey: '',
            handoffBuildChoiceVisible: false,
            handoffPendingItem: null,
            handoffAwaitingSave: false,
            handoffSaveContext: null
        }
    },
    computed: {
        ...mapGetters([
            'getLayout',
            'getItems',
            'getCompareList',
            'getFetchedItems',
            'allItemsLoaded',
            'getCompatibleMode',
            'getCompatLib',
            'getBuild',
            'getBuildTDP',
            'allMenuOptions',
            'getCurrentPartType',
            'loggedIn'
        ]),
        matchingItemsBeforeCompatibility: function(){
            return this.getFilteredList(this.selectableCatalogItems, this.activeCatalogFilters, false)
        },
        filteredItems: function(){
            var filtList = this.getFilteredList(this.selectableCatalogItems, this.activeCatalogFilters, true)
            return filtList
        },
        selectableCatalogItems: function(){
            return Array.isArray(this.getFetchedItems)
                ? this.getFetchedItems.filter((item) => isPartAvailable(item))
                : []
        },
        hasCompatibilityHiddenResults: function(){
            return this.getCompatibleMode == true &&
                this.filteredItems.length == 0 &&
                this.matchingItemsBeforeCompatibility.length > 0
        },
        compatibilityHiddenCount: function(){
            return this.matchingItemsBeforeCompatibility.length
        },
        compatibilityBlockers: function(){
            return this.getCompatibilityBlockers(this.matchingItemsBeforeCompatibility)
        },
        catalogPartType: function(){
            return this.normalizePartType(this.type || this.$route.name || this.getType())
        },
        catalogFilterGroups: function(){
            return this.buildCatalogFilterGroups(this.selectableCatalogItems, this.filterDefinitionsForType(this.catalogPartType))
        },
        totalCatalogItems: function(){
            return this.selectableCatalogItems.length
        },
        hasActiveCatalogFilters: function(){
            return Object.keys(this.activeCatalogFilters || {}).some((key) => {
                return Array.isArray(this.activeCatalogFilters[key]) && this.activeCatalogFilters[key].length > 0
            })
        },
        isItemsReady: function(){
            // NOTE: must be strict — `false == ''` is true in JS, which made this
            // always report "ready" and the loading overlay never showed.
            return this.allItemsLoaded === true || this.allItemsLoaded === ''
        },
        isCategoryLoading: function(){
            return !this.isItemsReady
        },
        handoffBuildPartCount: function(){
            return this.currentBuildItems().length
        },
        searchTerm: function(){
            return this.search ? this.search.trim() : ''
        },
        partTypeLabel: function(){
            var type = this.type || this.$route.path.replace(/\\|\/builder\//g,'')
            if(type == 'cpu'){
                return 'processors'
            }
            if(type == 'gpu' || type == 'graphics'){
                return 'graphics cards'
            }
            if(type == 'psu'){
                return 'power supplies'
            }
            return type ? type + ' parts' : 'parts'
        },
        showHandoffNextStep: function(){
            return this.$route.query && this.$route.query.handoffNext == '1'
        },
        handoffNextTitle: function(){
            var labels = {
                cpu: 'Choose a processor.',
                motherboard: 'Choose a motherboard.',
                ram: 'Choose memory.',
                storage: 'Choose storage.',
                graphics: 'Choose a graphics card.',
                psu: 'Choose a power supply.',
                case: 'Choose a case.',
            }
            return labels[this.catalogPartType] || 'Choose the next part.'
        },
        handoffNextDescription: function(){
            if(!this.isItemsReady){
                return 'Finding parts that fit your current build…'
            }
            var count = this.filteredItems.length
            if(this.catalogPartType == 'motherboard'){
                var cpu = this.selectedSlotItems('cpu')[0]
                var socket = cpu && cpu.compat
                    ? (cpu.compat.socket || cpu.compat.cpu_socket || cpu.compat.processor_socket)
                    : ''
                return count + ' ' + (socket ? socket + ' ' : 'compatible ') + (count == 1 ? 'board fits.' : 'boards fit.')
            }
            return count + ' compatible ' + this.partTypeLabel + ' fit your current build.'
        },
        scrollToTop() {
           return window.scrollTo(0,0);
        },
    },
    methods: {       
         ...mapActions([
            'getLastRoute',
            'fetchItems',
            'setCurrentPartType',
            'setLayout',
            'checkPartCompat',
            'itemsLoaded',
            'compatMode',
            'replaceBuildSlot',
            'restoreBuildSlot',
            'startBuildWithPart',
            'restoreBuildSnapshot',
        ]),
        extendList(){
            EventBus.$emit('extendList', 'status')
        },
        normalizePartType(type){
            var normalized = String(type || '').toLowerCase()
            if(normalized == 'gpu') return 'graphics'
            if(normalized == 'processor') return 'cpu'
            if(normalized == 'power supply') return 'psu'
            return normalized
        },
        isBuilderPartRoute(type){
            return ['cpu', 'motherboard', 'ram', 'storage', 'gpu', 'psu', 'case'].includes(type)
        },
        loadCategory(type){
            var currentType = this.normalizePartType(type || this.getType())
            this.type = type || this.type
            this.setCurrentPartType(currentType)
            this.itemsLoaded(false)
            this.fetchItems(currentType)
        },
        selectedSlotItems(type){
            var definition = getPartDefinition(type)
            var option = definition && this.allMenuOptions
                ? this.allMenuOptions[definition.menuKey]
                : null
            var contents = option && option.item ? option.item.contents : null
            if(!contents){ return [] }
            return Array.isArray(contents) ? contents.filter(Boolean) : [contents]
        },
        currentBuildItems(){
            var build = Array.isArray(this.getBuild) ? this.getBuild : []
            return build.reduce((items, slot) => {
                var contents = slot ? slot.contents : null
                if(Array.isArray(contents)){
                    return items.concat(contents.filter(Boolean))
                }
                if(contents){ items.push(contents) }
                return items
            }, [])
        },
        samePart(left, right){
            if(!left || !right){ return false }
            if(left.id !== undefined && right.id !== undefined){
                return String(left.id) == String(right.id)
            }
            return left.asin && right.asin && String(left.asin) == String(right.asin)
        },
        partName(item){
            return String((item && (item.model || item.name || item.title)) || 'Part')
        },
        handoffItem(){
            var id = this.$route.query && this.$route.query.id
            if(id === undefined || id === null || !Array.isArray(this.getFetchedItems)){
                return null
            }
            return this.getFetchedItems.find((item) => item && item.id !== undefined && String(item.id) == String(id)) || null
        },
        consumeBuilderHandoff(){
            if(!this.$route.query || !this.$route.query.handoff){ return }
            var query = Object.assign({}, this.$route.query)
            delete query.handoff
            this.$router.replace({ query: query }).catch(()=>{})
        },
        handoffContext(){
            return {
                source: 'builder-handoff',
                key: this.processedHandoffKey,
            }
        },
        cancelHandoffChoice(){
            this.handoffBuildChoiceVisible = false
            this.handoffPendingItem = null
            this.handoffAwaitingSave = false
            this.handoffSaveContext = null
            this.consumeBuilderHandoff()
        },
        async startCleanHandoff(){
            var item = this.handoffPendingItem
            if(!item){ return }
            this.handoffBuildChoiceVisible = false
            this.handoffAwaitingSave = false
            this.handoffSaveContext = null
            var previous = await this.startBuildWithPart(item)
            if(!previous){
                this.showHandoffToast('This part could not be added to the builder.')
                this.consumeBuilderHandoff()
                return
            }
            this.showHandoffToast(this.partName(item) + ' added to a new build', async () => {
                await this.restoreBuildSnapshot(previous)
            })
            this.handoffPendingItem = null
            this.openHandoffNextStep(item)
        },
        saveCurrentBuildFirst(){
            if(!this.handoffPendingItem){ return }
            if(this.loggedIn != 'true'){
                this.handoffBuildChoiceVisible = false
                this.$router.push({
                    path: '/users/login',
                    query: { redirect: this.$route.fullPath },
                }).catch(()=>{})
                return
            }
            this.handoffBuildChoiceVisible = false
            this.handoffAwaitingSave = true
            this.handoffSaveContext = this.handoffContext()
            EventBus.$emit('request-build-save', this.handoffSaveContext)
        },
        async handleHandoffSaveSucceeded(context){
            if(!this.handoffAwaitingSave || !this.handoffPendingItem || !context || context.key != this.processedHandoffKey){
                return
            }
            await this.startCleanHandoff()
        },
        handleHandoffSaveCancelled(context){
            if(!this.handoffAwaitingSave || !this.handoffPendingItem || !context || context.key != this.processedHandoffKey){
                return
            }
            this.handoffAwaitingSave = false
            this.handoffSaveContext = null
            this.handoffBuildChoiceVisible = true
        },
        showHandoffToast(message, undo){
            EventBus.$emit('builder-handoff-toast', {
                message: message,
                undo: undo || null,
            })
        },
        nextHandoffType(type){
            var next = {
                cpu: 'motherboard',
                motherboard: 'ram',
                ram: 'storage',
                storage: 'graphics',
                graphics: 'case',
                psu: '',
                case: 'psu',
            }
            return next[this.normalizePartType(type)] || ''
        },
        openHandoffNextStep(item){
            var fromType = this.normalizePartType(item.type)
            var nextType = this.nextHandoffType(fromType)
            this.compatMode(true)
            if(!nextType){
                this.$router.replace({ path: '/builder/home' }).catch(()=>{})
                return
            }
            var routeType = nextType == 'graphics' ? 'gpu' : nextType
            this.$router.replace({
                path: '/builder/' + routeType,
                query: { handoffNext: '1', from: fromType },
            }).catch(()=>{})
        },
        async processBuilderHandoff(){
            if(!this.$route.query || this.$route.query.handoff != 'add' || !this.isItemsReady){
                return
            }
            var key = String(this.$route.name) + ':' + String(this.$route.query.id || '')
            if(this.handoffProcessing || this.processedHandoffKey == key){ return }
            this.handoffProcessing = true
            this.processedHandoffKey = key

            try {
                var item = this.handoffItem()
                if(!item || !isPartAvailable(item)){
                    this.showHandoffToast('This part could not be added to the builder.')
                    this.consumeBuilderHandoff()
                    return
                }

                var existingItems = this.selectedSlotItems(item.type)
                if(existingItems.some((existing) => this.samePart(existing, item))){
                    this.showHandoffToast(this.partName(item) + ' is already in your build.')
                    this.openHandoffNextStep(item)
                    return
                }

                if(this.currentBuildItems().length){
                    this.handoffPendingItem = item
                    this.handoffBuildChoiceVisible = true
                    return
                }

                var previous = await this.replaceBuildSlot(item)
                if(!previous){
                    this.showHandoffToast('This part could not be added to the builder.')
                    this.consumeBuilderHandoff()
                    return
                }

                this.showHandoffToast(this.partName(item) + ' added to your build', async () => {
                    await this.restoreBuildSlot(previous)
                })
                this.openHandoffNextStep(item)
            }
            finally {
                this.handoffProcessing = false
            }
        },
        checkPartCompat:    ({compatTable, type, asin}) => functions.checkPartCompat({compatTable, type, asin}),
        compatibilityTableForItem(item){
            var itemTable = item && item.asin && this.getCompatLib ? this.getCompatLib[item.asin] : null
            if(!itemTable){
                return null
            }
            var type = this.normalizePartType(item.type)
            return itemTable[type] ||
                (type == 'graphics' ? itemTable.gpu : null) ||
                (type == 'gpu' ? itemTable.graphics : null)
        },
        selectedBuildItems(type){
            var normalized = this.normalizePartType(type)
            var aliases = normalized == 'graphics' ? ['graphics', 'gpu'] : [normalized]
            var slot = null
            for(var i = 0; i < aliases.length; i++){
                if(this.allMenuOptions && this.allMenuOptions[aliases[i]]){
                    slot = this.allMenuOptions[aliases[i]]
                    break
                }
            }
            var contents = slot && slot.item ? slot.item.contents : null
            if(!contents){
                return []
            }
            return Array.isArray(contents) ? contents.filter((item) => item) : [contents]
        },
        selectedPartTypeLabel(type){
            var labels = {
                cpu: 'Processor',
                motherboard: 'Motherboard',
                ram: 'Memory',
                storage: 'Storage',
                graphics: 'Graphics card',
                psu: 'Power supply',
                case: 'Case',
                cooling: 'Cooler',
                general: 'Build'
            }
            var normalized = this.normalizePartType(type)
            return labels[normalized] || this.titleFilterValue(normalized)
        },
        selectedPartName(item, type){
            if(this.normalizePartType(type) == 'general'){
                return 'Current build load'
            }
            var model = item && (item.model || item.name)
            var brand = item && (item.brand || (item.general && item.general.brand))
            if(!model){
                return brand || this.selectedPartTypeLabel(type)
            }
            if(brand && String(model).toLowerCase().indexOf(String(brand).toLowerCase()) < 0){
                return brand + ' ' + model
            }
            return model
        },
        compatibilitySpecLabel(option){
            var labels = {
                socket: 'Socket',
                processor_generation: 'CPU generation',
                ram_type: 'Memory type',
                ram_speed: 'Memory speed',
                max_memory: 'Memory capacity',
                number_of_items: 'Module count',
                ram_slots: 'Memory slots',
                form_factor: 'Form factor',
                pci_e_x16: 'PCIe x16 slots',
                interface: 'Interface',
                sata: 'SATA slots',
                sata_slots: 'SATA slots',
                'm.2_slots': 'M.2 slots',
                power: 'Required power'
            }
            return labels[option] || this.titleFilterValue(String(option || '').replace(/_/g, ' '))
        },
        compatibilitySpecValue(item, type, option){
            if(this.normalizePartType(type) == 'general' && option == 'power'){
                return this.getBuildTDP ? this.getBuildTDP + ' W' : ''
            }
            var normalizedType = this.normalizePartType(type)
            var pathsByOption = {
                socket: ['compat.socket', 'specs.Socket'],
                processor_generation: ['compat.processor_generation', 'specs.Processor_Generation'],
                ram_type: normalizedType == 'ram'
                    ? ['compat.ram_type', 'specs.RAM_Type']
                    : ['compat.ram', 'compat.ram_type', 'specs.RAM_Type'],
                ram_speed: ['compat.ram_speed', 'specs.RAM_Speed'],
                max_memory: normalizedType == 'ram'
                    ? ['compat.capacity', 'specs.Capacity']
                    : ['specs.Max_Memory_Size', 'compat.max_memory'],
                number_of_items: ['compat.number_of_items', 'specs.Number_of_Sticks'],
                ram_slots: ['compat.ram_slots'],
                form_factor: ['compat.form_factor', 'general.form_factor', 'specs.form_factor'],
                pci_e_x16: ['compat.pci_e_x16'],
                interface: ['compat.compat_interface', 'specs.hardware_interface'],
                sata: ['compat.sata_slots'],
                sata_slots: ['compat.sata_slots'],
                'm.2_slots': ['compat.M_2_Slot']
            }
            var paths = pathsByOption[option] || ['compat.' + option, 'specs.' + option, 'general.' + option]
            for(var i = 0; i < paths.length; i++){
                var value = this.filterPathValue(item, paths[i])
                if(value !== undefined && value !== null && value !== ''){
                    return this.formatCompatibilitySpecValue(value)
                }
            }
            return ''
        },
        formatCompatibilitySpecValue(value){
            if(Array.isArray(value)){
                return value.join(', ')
            }
            var text = String(value || '').trim()
            if(/^\[/.test(text)){
                try {
                    var parsed = JSON.parse(text)
                    if(Array.isArray(parsed)){
                        return parsed.join(', ')
                    }
                }
                catch(e) { /* use the original value */ }
            }
            return text
        },
        getCompatibilityBlockers(items){
            var blockers = {}
            ;(items || []).forEach((candidate) => {
                var typeTable = this.compatibilityTableForItem(candidate)
                if(!typeTable){
                    return
                }
                Object.keys(typeTable).forEach((withType) => {
                    var optionTable = typeTable[withType] || {}
                    Object.keys(optionTable).forEach((option) => {
                        var issue = optionTable[option]
                        if(!issue || issue.compatible !== false){
                            return
                        }
                        var selectedItems = this.selectedBuildItems(withType)
                        if(!selectedItems.length && this.normalizePartType(withType) == 'general'){
                            selectedItems = [{ id: 'build-power', type: 'general' }]
                        }
                        selectedItems.forEach((selectedItem, index) => {
                            var itemKey = selectedItem.asin || selectedItem.id || selectedItem.model || index
                            var key = this.normalizePartType(withType) + ':' + itemKey
                            if(!blockers[key]){
                                blockers[key] = {
                                    key: key,
                                    typeLabel: this.selectedPartTypeLabel(withType),
                                    name: this.selectedPartName(selectedItem, withType),
                                    specs: {}
                                }
                            }
                            if(!blockers[key].specs[option]){
                                blockers[key].specs[option] = {
                                    key: option,
                                    label: this.compatibilitySpecLabel(option),
                                    value: this.compatibilitySpecValue(selectedItem, withType, option),
                                    message: issue.message || ''
                                }
                            }
                        })
                    })
                })
            })
            return Object.keys(blockers).map((key) => {
                var blocker = blockers[key]
                blocker.specs = Object.keys(blocker.specs).map((option) => blocker.specs[option])
                return blocker
            })
        },
        filterDefinitionsForType(type){
            var definitions = {
                cpu: [
                    { key: 'socket', label: 'Socket', paths: ['compat.socket', 'specs.Socket'], quick: true },
                    { key: 'cpuFamily', label: 'CPU family', paths: ['model'], quick: true },
                    { key: 'cores', label: 'Core count', paths: ['specs.Number_of_Cores'], quick: true },
                    { key: 'brand', label: 'Brand', paths: ['brand', 'general.brand', 'compat.brand'] },
                    { key: 'ramType', label: 'Memory support', paths: ['compat.ram', 'specs.RAM_Type'], multiple: true }
                ],
                motherboard: [
                    { key: 'socket', label: 'CPU socket', paths: ['specs.Socket', 'compat.socket'], quick: true },
                    { key: 'formFactor', label: 'Form factor', paths: ['form_factor', 'general.form_factor', 'specs.form_factor', 'compat.form_factor'], quick: true },
                    { key: 'ramType', label: 'Memory type', paths: ['compat.ram', 'specs.Compatible_Memory', 'specs.RAM_Type'], multiple: true, quick: true },
                    { key: 'chipset', label: 'Chipset', paths: ['specs.Chipset', 'compat.chipset'] },
                    { key: 'wifi', label: 'Wi-Fi', paths: ['specs.WiFi'], preferredValues: ['Yes'] }
                ],
                ram: [
                    { key: 'capacity', label: 'Capacity', paths: ['specs.Capacity', 'compat.capacity'], defaultUnit: 'GB', quick: true },
                    { key: 'ramType', label: 'Memory type', paths: ['specs.RAM_Type', 'compat.ram_type'], multiple: true, quick: true },
                    { key: 'speed', label: 'Speed', paths: ['specs.RAM_Speed', 'compat.ram_speed'], defaultUnit: 'MHz', quick: true },
                    { key: 'sticks', label: 'Kit size', paths: ['specs.Number_of_Sticks', 'number_of_items', 'compat.number_of_items'] },
                    { key: 'brand', label: 'Brand', paths: ['brand', 'general.brand'] }
                ],
                storage: [
                    { key: 'driveType', label: 'Drive type', paths: ['specs.Disk_Type', 'compat.Disk_Type'], quick: true },
                    { key: 'capacity', label: 'Capacity', paths: ['specs.Capacity', 'compat.capacity', 'compat.copacity'], quick: true },
                    { key: 'interface', label: 'Interface', paths: ['specs.hardware_interface', 'compat.compat_interface'], multiple: true, quick: true },
                    { key: 'formFactor', label: 'Form factor', paths: ['specs.size', 'compat.size'] },
                    { key: 'brand', label: 'Brand', paths: ['brand', 'general.brand'] }
                ],
                graphics: [
                    { key: 'gpuFamily', label: 'GPU model', paths: ['specs.graphics_processor', 'model', 'general.model'], quick: true },
                    { key: 'vram', label: 'VRAM', paths: ['specs.graphics_ram_capacity'], defaultUnit: 'GB', quick: true },
                    { key: 'brand', label: 'Brand', paths: ['brand', 'general.brand'], quick: true },
                    { key: 'ramType', label: 'Memory type', paths: ['specs.graphics_ram_type'] },
                    { key: 'interface', label: 'Interface', paths: ['specs.graphics_interface', 'compat.graphics_interface'] }
                ],
                psu: [
                    { key: 'wattage', label: 'Wattage', paths: ['specs.wattage', 'compat.wattage'], defaultUnit: 'W', quick: true },
                    { key: 'rating', label: 'Efficiency rating', paths: ['specs.energy_rating'], quick: true },
                    { key: 'modular', label: 'Modularity', paths: ['specs.modular'], quick: true },
                    { key: 'formFactor', label: 'Form factor', paths: ['compat.form_factor'] },
                    { key: 'brand', label: 'Brand', paths: ['brand', 'general.brand'] }
                ],
                case: [
                    { key: 'formFactor', label: 'Motherboard support', paths: ['general.form_factor', 'specs.form_factor', 'compat.form_factor'], multiple: true, quick: true },
                    { key: 'brand', label: 'Brand', paths: ['brand', 'general.brand'], quick: true },
                    { key: 'temperedGlass', label: 'Tempered glass', paths: ['specs.tempered_glass', 'compat.tempered_glass'], preferredValues: ['Yes'], quick: true },
                    { key: 'color', label: 'Color', paths: ['specs.color', 'compat.color'] }
                ]
            }
            return definitions[type] || []
        },
        filterPathValue(item, path){
            return String(path || '').split('.').reduce((value, key) => {
                return value && value[key] !== undefined ? value[key] : undefined
            }, item)
        },
        rawFilterValues(item, definition){
            var values = []
            ;(definition.paths || []).forEach((path) => {
                var raw = this.filterPathValue(item, path)
                if(raw === undefined || raw === null || raw === ''){
                    return
                }
                if(Array.isArray(raw)){
                    values = values.concat(raw)
                    return
                }
                if(typeof raw == 'string' && /^\s*\[/.test(raw)){
                    try {
                        var parsed = JSON.parse(raw)
                        if(Array.isArray(parsed)){
                            values = values.concat(parsed)
                            return
                        }
                    }
                    catch(e) { /* use the original value */ }
                }
                if(definition.multiple && typeof raw == 'string' && /[,|/]/.test(raw)){
                    values = values.concat(raw.split(/[,|/]+/))
                    return
                }
                values.push(raw)
            })
            return values
        },
        normalizeFilterValue(value, definition){
            if(value === undefined || value === null || typeof value == 'object'){
                return ''
            }
            var text = String(value).replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
            if(!text || /^(n\/a|na|null|undefined|none|unknown|-|--)$/i.test(text)){
                return ''
            }
            var key = definition.key
            var number
            var match

            if(key == 'socket'){
                return text.toUpperCase().replace(/^SOCKET\s+/i, '').replace(/\s+/g, '')
            }
            if(key == 'ramType'){
                match = text.match(/DDR\s*[2-6]/i)
                return match ? match[0].toUpperCase().replace(/\s+/g, '') : text.toUpperCase()
            }
            if(key == 'cores'){
                match = text.match(/\d+/)
                return match ? Number(match[0]) + ' cores' : text
            }
            if(key == 'cpuFamily'){
                match = text.match(/(?:Ryzen\s*[3579]|Core\s*Ultra\s*[3579]|Core\s*i[3579]|Threadripper|Xeon)/i)
                return match ? this.titleFilterValue(match[0]) : text
            }
            if(key == 'gpuFamily'){
                match = text.match(/\b(?:RTX|GTX)\s*\d{3,4}(?:\s*Ti|\s*SUPER)?\b/i) ||
                    text.match(/\bRX\s*\d{3,4}(?:\s*XT|\s*XTX)?\b/i) ||
                    text.match(/\bArc\s*[AB]\d{3}\b/i)
                return match ? match[0].toUpperCase().replace(/\s+/g, ' ') : text
            }
            if(key == 'formFactor'){
                var compact = text.toLowerCase().replace(/[^a-z0-9]/g, '')
                var formFactors = {
                    atx: 'ATX',
                    microatx: 'Micro-ATX',
                    matx: 'Micro-ATX',
                    miniitx: 'Mini-ITX',
                    mitx: 'Mini-ITX',
                    eatx: 'E-ATX'
                }
                return formFactors[compact] || text
            }
            if(key == 'capacity' || key == 'vram'){
                match = text.match(/([\d.]+)\s*(TB|GB|MB)?/i)
                if(match){
                    return (this.cleanFilterNumber(match[1]) + ' ' + String(match[2] || definition.defaultUnit || '').toUpperCase()).trim()
                }
            }
            if(key == 'speed'){
                match = text.match(/[\d.]+/)
                return match ? this.cleanFilterNumber(match[0]) + ' ' + (definition.defaultUnit || 'MHz') : text
            }
            if(key == 'wattage'){
                match = text.match(/[\d.]+/)
                return match ? this.cleanFilterNumber(match[0]) + ' W' : text
            }
            if(key == 'sticks'){
                match = text.match(/\d+/)
                return match ? Number(match[0]) + (Number(match[0]) === 1 ? ' stick' : ' sticks') : text
            }
            if(key == 'wifi' || key == 'temperedGlass'){
                if(/^(yes|true|1|included)$/i.test(text)) return 'Yes'
                if(/^(no|false|0|not included)$/i.test(text)) return 'No'
            }
            if(key == 'brand'){
                return this.titleFilterValue(text)
            }
            number = Number(text)
            return isNaN(number) ? text : String(number)
        },
        cleanFilterNumber(value){
            var number = Number(value)
            return isNaN(number) ? String(value) : String(Number(number.toFixed(2)))
        },
        titleFilterValue(value){
            return String(value || '').replace(/\b\w/g, (letter) => letter.toUpperCase())
        },
        itemFilterValues(item, definition){
            var seen = {}
            return this.rawFilterValues(item, definition)
                .map((value) => this.normalizeFilterValue(value, definition))
                .filter((value) => {
                    var key = value.toLowerCase()
                    if(!value || seen[key]) return false
                    seen[key] = true
                    return true
                })
        },
        buildCatalogFilterGroups(items, definitions){
            return (definitions || []).map((definition) => {
                var counts = {}
                var labels = {}
                ;(items || []).forEach((item) => {
                    this.itemFilterValues(item, definition).forEach((value) => {
                        var key = value.toLowerCase()
                        counts[key] = (counts[key] || 0) + 1
                        labels[key] = labels[key] || value
                    })
                })
                var options = Object.keys(counts).map((key) => ({
                    value: labels[key],
                    label: labels[key],
                    count: counts[key]
                })).sort((left, right) => {
                    return right.count - left.count || left.label.localeCompare(right.label, undefined, { numeric: true })
                })
                return Object.assign({}, definition, { options: options })
            }).filter((group) => group.options.length)
        },
        toggleCatalogFilter(groupKey, value){
            var next = Object.assign({}, this.activeCatalogFilters)
            var values = Array.isArray(next[groupKey]) ? next[groupKey].slice() : []
            var index = values.indexOf(value)
            if(index >= 0){
                values.splice(index, 1)
            }
            else {
                values.push(value)
            }
            if(values.length){
                next[groupKey] = values
            }
            else {
                delete next[groupKey]
            }
            this.activeCatalogFilters = next
        },
        clearCatalogFilters(){
            this.activeCatalogFilters = {}
        },
        matchesCatalogFilters(item, filters){
            var activeKeys = Object.keys(filters || {}).filter((key) => {
                return Array.isArray(filters[key]) && filters[key].length
            })
            if(!activeKeys.length) return true
            var definitions = this.filterDefinitionsForType(this.catalogPartType)
            return activeKeys.every((key) => {
                var definition = definitions.find((entry) => entry.key == key)
                if(!definition) return true
                var itemValues = this.itemFilterValues(item, definition)
                return filters[key].some((value) => itemValues.indexOf(value) >= 0)
            })
        },
        getFilteredList(items, filters, applyCompatibility) {
            var search = this.search ? this.search.trim() : ''
            var multiQueryParams = search.split(/[-,_ ]+/)
            multiQueryParams = multiQueryParams.filter(itm => itm !== '' && itm !== ' ')
            var safeString = function(value){
                if(value === undefined || value === null){
                    return ''
                }
                return String(value).toLowerCase().split(' ').join('')
            }
            var safeJson = function(value){
                try {
                    return JSON.stringify(value || {})
                }
                catch(e) {
                    return ''
                }
            }
            var matchesSearch = function(item, terms){
                var haystack = [
                    item.model,
                    item.asin,
                    item.upc,
                    item.brand,
                    item.description,
                    item.type,
                    safeJson(item.specs),
                    safeJson(item.compat),
                    safeJson(item)
                ].map(safeString).join(' ')

                return terms.every((term) => {
                    return haystack.includes(safeString(term))
                })
            }
            var result = (items || []).filter((item) => isPartAvailable(item))
            if(items && items !== null && items.length >0){
                if (multiQueryParams.length > 0) {
                    result = result.filter((item) => {
                        return matchesSearch(item, multiQueryParams)
                    })
                }
                if(filters && Object.keys(filters).length){
                    result = result.filter((item) => this.matchesCatalogFilters(item, filters))
                }
                if(applyCompatibility !== false && this.getCompatibleMode == true){
                    return result.filter((item) => {
                        return this.checkPartCompat({compatTable: this.getCompatLib, type: item.type, asin: item.asin}) !==false
                    })
                }
                return result
            }
            return []
        },
        popup(){
            this.setLayout('advanced')
        },
        syncLayoutFromRoute(){
            if(!this.isBuilderPartRoute(this.$route.name)){
                return
            }
            var query = this.$route.query || {}
            if(query.view == 'slider' || query.redir){
                this.setLayout('advanced')
                return
            }
            this.setLayout('simple')
        },
        onSubmit(){
            const query = this.search ? this.search.trim() : ''
            if (query) {
                analytics.trackSearch(query, { partType: this.getType(), resultCount: this.filteredItems.length })
            }
        },
        clearSearch(){
            this.search = ''
        },
        showIncompatibleResults(){
            this.compatMode(false)
        },
        syncLocalItems(){
            this.items = this.getFetchedItems
        },
        getType(){
            this.type = this.$route.path.replace(/\\|\/builder\//g,'') 
            return this.type
        },
        updateRoute(){
            this.getLastRoute(this.$route.path)
        },
        frequency(spec){
            var maxFrequency = 5.2;
            var currentFrequency = parseFloat(spec);
            var frequencyIndex = currentFrequency*100/maxFrequency
            EventBus.$emit('frequency', frequencyIndex)
            return frequencyIndex +"%"
        },
        updateCompareList(){
            this.compareList = this.getCompareList
        },
        getAttribs(){
            if(this.getFetchedItems !== null){
                this.attribs = this.getFetchedItems.attribs
            }
        },
    },
    mounted() {
        this.syncLayoutFromRoute()
        this.syncLocalItems()
        this.getType();
        EventBus.$on('frequency', (frequencyIndex) => {
            this.frequencyGHZ = parseInt(parseFloat(frequencyIndex)*100);
            parseInt(this.frequencyGHZ)
            console.log('frequency emited, frequencyGHZ: '+this.frequencyGHZ)
        });
        EventBus.$on('emitTypeChange', () => {
            this.syncLocalItems()
            this.search = ''
        });
        EventBus.$on('extendListResponse', (bool) => {
            this.listExtended = bool        
        })
        EventBus.$on('build-save-succeeded', this.handleHandoffSaveSucceeded)
        EventBus.$on('build-save-cancelled', this.handleHandoffSaveCancelled)
        this.processBuilderHandoff()
    },
    updated(){
        this.updateRoute(); 
        this.updateCompareList()
    },
    created () {
        this.loadCategory(this.getType())
        
        this.updateRoute();
        
        if(this.allItemsLoaded){
            console.log(this.$store.getters.getFetchedItems)
        }
        this.getAttribs();
    },
    beforeDestroy(){
        EventBus.$off('build-save-succeeded', this.handleHandoffSaveSucceeded)
        EventBus.$off('build-save-cancelled', this.handleHandoffSaveCancelled)
    }
}
</script>

<style scoped>
.brands {
    margin-bottom: 0.35rem;
}

.builder-catalog-content {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
}

/* The old catalog list deliberately extended eight pixels beyond its parent.
   Inside the shared builder frame that makes the final card and its actions
   cross the right edge, so every catalog view now respects the same boundary. */
.builder-catalog-content .item-list {
    margin-right: 0;
}

.brands h4 {
    margin-bottom: 0;
    letter-spacing: 0.04em;
}

.handoff-next-step {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin: 0.35rem 0 0.65rem;
    padding: 0.75rem 0.9rem;
    border: 1px solid rgba(32, 184, 235, 0.38);
    border-radius: 8px;
    background:
        radial-gradient(circle at 0 50%, rgba(32, 184, 235, 0.13), transparent 36%),
        rgba(7, 14, 22, 0.76);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.handoff-next-step__kicker {
    flex: 0 0 auto;
    padding: 0.28rem 0.48rem;
    border: 1px solid rgba(32, 184, 235, 0.42);
    border-radius: 999px;
    color: #32c3ef;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.handoff-next-step strong {
    color: #e6ebf3;
    font-size: 14px;
}

.handoff-next-step p {
    margin: 0.12rem 0 0;
    color: #95a1b4;
    font-size: 12px;
}

.searchbar-section {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    margin-bottom: 0.45rem;
}

/* Search keeps to its own column so the filter bar sits on the same line. */
.catalog-controls {
    position: relative;
    z-index: 200;
    display: grid;
    grid-template-columns: minmax(250px, 360px) minmax(0, 1fr);
    align-items: start;
    gap: 14px;
    min-width: 0;
}

/* The sort toolbar (smallnav) sits at z-index 11000 so it clears the catalog
   cards. That made it a sibling this row could never beat while the "All
   filters" panel was open -- the panel's own z-index is trapped in this
   element's stacking context. Outrank the toolbar only while the panel is open,
   and stay under the feedback dialog at 12000.
   z-index ONLY: putting layout here left the collapsed state without a grid. */
.catalog-controls.filters-expanded {
    z-index: 11500;
}

.catalog-filters {
    min-width: 0;
}

.searchbar {
    grid-column: 1;
    grid-row: 1;
    width: 100%;
    margin: 0 !important;
}

.searchbar input {
    height: 32px;
    max-height: none !important;
    padding: 0 0.85rem;
    border: 1px solid rgba(67, 72, 87, 0.82);
    border-radius: 8px;
    background: rgba(67, 72, 87, 0.9);
    color: #eef2f8;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.searchbar input::placeholder {
    color: #98a1b4;
}

.items-display {
    margin-top: 0 !important;
}

/* List and Details must occupy the same box, or switching between them moves the
   page and the checklist beside it. The list was pinned to 71vh while the slider
   was purely content-sized, so the two never lined up.
   Declared here as a custom property because .item-list lives in the global
   builder.css and inherits it from this ancestor. */
.builder-display-frame {
    position: relative;
    --gc-display-height: clamp(520px, 71vh, 820px);
}

@media (min-width: 761px) {
    .builder-display-frame {
        min-height: var(--gc-display-height);
    }
}

@media (min-width: 1201px) {
    .builder-catalog-content {
        padding-right: 18px;
        padding-left: 18px;
    }
}

.builder-content-underlay {
    transition:
        opacity 0.18s ease,
        filter 0.18s ease;
}

.builder-content-underlay.is-dimmed {
    opacity: 0.42;
    filter: saturate(0.72) blur(0.35px);
    pointer-events: none;
}

.builder-empty-state {
    min-height: 265px;
    margin: 0.65rem 0 0;
    padding: 3.2rem 1.5rem;
    border: 1px solid rgba(67, 72, 87, 0.82);
    border-radius: 8px;
    background:
        radial-gradient(circle at 50% 0%, rgba(25, 151, 198, 0.1), transparent 34%),
        rgba(7, 10, 16, 0.58);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.035),
        0 14px 30px rgba(0, 0, 0, 0.16);
    color: #cfd2da;
    text-align: center;
}

.builder-empty-state__eyebrow {
    margin-bottom: 0.5rem;
    color: #1997c6;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.builder-empty-state__title {
    color: #aeb8c9;
    font-size: 24px;
    font-weight: 600;
}

.builder-empty-state__count {
    appearance: none;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #48bfe3;
    cursor: pointer;
    font: inherit;
    font-weight: 800;
    white-space: nowrap;
}

.builder-empty-state__count:hover {
    color: #74d7f2;
    text-decoration: underline;
    text-underline-offset: 3px;
}

.builder-empty-state__count:focus-visible {
    border-radius: 3px;
    outline: 2px solid rgba(32, 184, 235, 0.72);
    outline-offset: 3px;
}

.builder-empty-state p {
    max-width: 430px;
    margin: 0.55rem auto 1.2rem;
    color: #98a1b4;
    font-size: 15px;
    line-height: 1.45;
}

.builder-empty-state__actions {
    display: flex;
    justify-content: center;
    gap: 8px;
}

.compatibility-blockers {
    width: min(760px, 100%);
    margin: -0.15rem auto 1.2rem;
}

.compatibility-blockers__label {
    margin-bottom: 0.5rem;
    color: #778195;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-align: center;
    text-transform: uppercase;
}

.compatibility-blockers__list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
}

.compatibility-blocker {
    flex: 1 1 230px;
    max-width: 370px;
    min-width: 0;
    padding: 0.6rem 0.7rem;
    border: 1px solid rgba(184, 138, 72, 0.34);
    border-radius: 7px;
    background: rgba(30, 24, 17, 0.28);
    text-align: left;
}

.compatibility-blocker__identity {
    display: flex;
    align-items: baseline;
    gap: 7px;
    min-width: 0;
    margin-bottom: 0.45rem;
}

.compatibility-blocker__identity > span {
    flex: 0 0 auto;
    color: #b99157;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.compatibility-blocker__identity > strong {
    min-width: 0;
    overflow: hidden;
    color: #aeb8c9;
    font-size: 12px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.compatibility-blocker__specs {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.compatibility-blocker__spec {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 22px;
    padding: 0 7px;
    border: 1px solid rgba(67, 72, 87, 0.66);
    border-radius: 999px;
    background: rgba(10, 13, 19, 0.58);
    color: #7f899b;
    font-size: 9px;
    white-space: nowrap;
}

.compatibility-blocker__spec > strong {
    color: #d0a860;
    font-weight: 800;
}

.builder-empty-state__note {
    display: block;
    margin-top: 0.7rem;
    color: #778195;
    font-size: 11px;
}

.builder-empty-state__button {
    min-width: 140px;
    height: 36px;
    padding: 0 24px;
    border: 1px solid rgba(25, 151, 198, 0.78);
    border-radius: 6px;
    background: rgba(25, 151, 198, 0.08);
    color: #1fb2e8;
    cursor: pointer;
    font-weight: 700;
    text-transform: uppercase;
    transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.builder-empty-state__button:hover,
.builder-empty-state__button:focus {
    border-color: #1fb2e8;
    background: rgba(25, 151, 198, 0.18);
    color: #eef2f8;
}

@media (max-width: 1200px) {
    .searchbar-section {
        gap: 0.45rem;
    }
}

@media (max-width: 980px) {
    .catalog-controls {
        grid-template-columns: 1fr;
        gap: 0.45rem;
    }

}

@media (min-width: 761px) and (max-width: 1200px) {
    .advanced,
    .left-side-page,
    .items-display,
    .slider-container {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
    }

    .searchbar-section {
        margin-bottom: 0.55rem;
    }

    .searchbar input {
        width: 100%;
    }

    .left-side-page,
    .builder-display-frame {
        max-width: 100%;
        overflow: visible;
    }

    .items-display {
        overflow: visible;
    }

    .demo.builder-display-frame {
        min-height: 0 !important;
        margin-right: 0 !important;
        margin-left: 0 !important;
    }
}

@media (max-width: 760px) {
    .brands {
        margin: 0.1rem 0 0.45rem;
    }

    .brands h4 {
        font-size: 17px !important;
        line-height: 1.2;
    }

    .searchbar-section {
        gap: 0.5rem;
        margin-bottom: 0.65rem;
    }

    .searchbar input {
        height: 38px;
        padding: 0 0.85rem;
        font-size: 14px;
    }

    .advanced,
    .left-side-page,
    .items-display,
    .slider-container {
        width: 100%;
        min-width: 0;
        min-height: 0;
    }

    .items-display {
        overflow: visible;
    }

    .demo.builder-display-frame {
        min-height: 0 !important;
        margin-right: 0 !important;
        margin-left: 0 !important;
    }

    .builder-empty-state {
        min-height: 220px;
        margin-top: 0.55rem;
        padding: 2rem 1rem;
    }

    .builder-empty-state__title {
        font-size: 20px;
    }
}
</style>
<style scoped>
@import '../../assets/css/builder.css';
.advanced {
    max-height: none;
}
::-webkit-scrollbar { 
   display:unset !important; 
}
.extend-list-button {
    text-align: center;
    padding-top: 5px;
    position: absolute;
    left: 0px;
    right: 0px;
    margin-top: 5px;
    height: 32px;
    cursor: pointer;
    font-size: 22px;
    transition: 0.2s;
    color: #9198aa
}
.extend-list-button:hover {
    color: #1997c6;
    font-size: 24px;
}

@media (max-width: 760px) {
    .advanced {
        max-height: none;
    }

    ::-webkit-scrollbar {
        width: 5px;
    }
}
</style>
